/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Flex} from '@brightcove/studio-components';

import Panel from '../Panel/Panel';
import Button from '../Button/Button';
import './Images.scss';
import { getUrlWithOptions } from '../../utils';
import { ImageData } from '../../interfaces';
import { useApi } from '../../hooks/useApi';
import { TextArea } from '../ControlledComponents';

interface ImagesProps {
  idPanel?: string;
  data: ImageData;
  endpointRootPath: string;
  onEditModeChange?: (val: boolean, isSavedData?: boolean) => void;
  disableEditing?: boolean;
  contentType: string;
}

const DEFAULT_IMAGE = 'https://img.brightcove.com/UIKitLogo-Cube.svg';
const getImageByType = (images: any, type: string) => {
  const lowerType = type.toLowerCase();
  return images[lowerType] ? images[lowerType]['src'] :
    lowerType !== 'vertical' ? DEFAULT_IMAGE: '';
};

const getImageDimensions = (url: string): Promise<{ width?: number, height?: number }> => {
  return new Promise((resolve) => {
    if (!url) {
      resolve({});
    } else {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({});
      img.src = url;
    }
  });
}

const Images: FC<ImagesProps> = ({ idPanel, data, endpointRootPath, onEditModeChange, disableEditing, contentType }) => {
  const { id } = data;
  const [images, setImages] = useState<any>(data?.images || {});
  const {  apiPatch } = useApi();
  const [editing, setEditing] = useState<boolean>(false);
  const [, setImagePoster] = useState<any>({
    image: getImageByType(images, 'Poster'),
    file: getImageByType(images, 'Poster'),
  });
  const [, setImageThumb] = useState<any>({
    image: getImageByType(images, 'Thumbnail'),
    file: getImageByType(images, 'Thumbnail'),
  });
  // const [, setImageHorizontal] = useState<any>({
  //   image: getImageByType(images, 'Horizontal'),
  //   file: getImageByType(images, 'Horizontal'),
  // });

  const[posterImage, setPosterImage] = useState('');
  const[thumbImage, setThumbImage] = useState('');
  // const[horizontalImage, setHorizontalImage] = useState('');
  const [savedData, setSavedData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [, setOriginalImages] = useState<any[]>([]);

  useEffect(() => {
    setImages(data?.custom_fields || []);
    setOriginalImages(data?.custom_fields || []);
  }, [data]);

  useEffect(() => {
    setImagePoster({
      image: getImageByType(images, 'Poster'),
      file: getImageByType(images, 'Poster'),
    });
    setImageThumb({
      image: getImageByType(images, 'Thumbnail'),
      file: getImageByType(images, 'Thumbnail'),
    });
    // setImageHorizontal({
    //   image: getImageByType(images, 'Horizontal'),
    //   file: getImageByType(images, 'Horizontal'),
    // });
    setPosterImage(getImageByType(images, 'Poster'));
    setThumbImage(getImageByType(images, 'Thumbnail'));
    // setHorizontalImage(getImageByType(images, 'Horizontal'));
  }, [images, editing]);

  useEffect(() => {
    onEditModeChange && onEditModeChange(editing, savedData);
  }, [editing]);

  const onSaveImages = async () => {
    setSaving(true);

    const [imagePosterUrl] = await Promise.all([
      await getUploadUrl(),
      // await getUploadUrl(thumbImage, 'Thumbnail'),
    ]);

    setSaving(false);

    if (imagePosterUrl) {
      setSavedData(true);
      setEditing(false);
    }
  };

  const getUploadUrl = async () => {
    const [thumbDimensions, posterDimensions] = await Promise.all([
      getImageDimensions(thumbImage),
      getImageDimensions(posterImage)
    ]);

    const body: any = {
      images: {
        poster: {
          src: posterImage,
          sources: [
            {
            src: posterImage,
            height: posterDimensions.height || 360,
            width: posterDimensions.width || 640
            }
          ]
        },
        thumbnail: {
          src: thumbImage,
          sources: [
            {
            src: thumbImage,
            height: thumbDimensions.height || 90,
            width: thumbDimensions.width || 160
            }
          ]
        }
      }
    };

    // TODO: Was accidentally added. Keep in case horizontal_image support is requested
    // if (contentType === CONTENT_SERIES) {
    //   body.custom_images = {
    //     vertical: {
    //       src: horizontalImage,
    //       sources: [
    //         {
    //         src: horizontalImage,
    //         height: verticalDimensions.height || 640,
    //         width: verticalDimensions.width || 360
    //         }
    //       ]
    //     }
    //   };
    // }

    const responseImage = await apiPatch(
      getUrlWithOptions(`${endpointRootPath}/${id}`),
      { body }
    );
    return responseImage?.data;

  };

  const onCancel = () => {
    setEditing(false);
  };

  const savingDisabled = useMemo(() => saving, [saving]);

  return (
    <div id={idPanel} className="Images">
      <Panel
        title="Images"
        actions={
          <>
            {editing ? (
              <>
                <Button variant="secondary-invert" text="Cancel" disabled={saving} onClick={onCancel} />
                <Button
                  variant="primary"
                  text={saving ? 'Saving...' : 'Save'}
                  disabled={savingDisabled}
                  onClick={onSaveImages}
                />
              </>
            ) : (
              <Button
                variant="secondary"
                text="Edit"
                onClick={() => {
                  setEditing(!editing);
                }}
                disabled={disableEditing}
              />
            )}
          </>
        }
      >
        <>
          {editing ? (
            <Flex className="ml-4">
              <div>
                  <p className="title">Poster</p>
                  <div>
                <TextArea
                  className="image-upload"
                  value={posterImage}
                  onChange={(updatedURL) => {
                    setPosterImage(updatedURL)
                  }}
                />
                </div>
              </div>
              <div className="ml-9">
                  <p className="title">Thumbnail</p>
                  <div>
                <TextArea
                  className="image-upload"
                  value={thumbImage}
                  onChange={(updatedURL) => {
                    setThumbImage(updatedURL)
                  }}
                />
                </div>
              </div>
              {/* { contentType === CONTENT_SERIES &&
                <div className="ml-9">
                    <p className="title">Horizontal</p>
                    <div>
                  <TextArea
                    className="image-upload"
                    value={horizontalImage}
                    onChange={(updatedURL) => {
                      setHorizontalImage(updatedURL)
                    }}
                  />
                  </div>
                </div>
              } */}
            </Flex>
          ) : (
            <Flex className="ml-4">
              <div>
                <p className="title">Poster</p>
                  <p className="description">{posterImage}</p>
              </div>
              <div className="ml-9">
                <p className="title">Thumbnail</p>
                <div className="description">{thumbImage}</div>
              </div>
              {/* {contentType === CONTENT_SERIES &&
                <div className="ml-9">
                  <p className="title">Horizontal</p>
                  <div className="description">{horizontalImage}</div>
                </div>
              } */}
            </Flex>
          )}
        </>
      </Panel>
    </div>
  );
};

export default Images;
